import React, { useEffect, useState } from 'react';
import { Card, CardFooter, CardBody, Row, Col } from 'reactstrap';
import { PageTitle, Button, Field, Table } from '@cordata/buffet';
import { withFormik } from 'formik';
import * as Yup from 'yup';

import { adminInstance as axiosAdmin, toastSuccess } from '~/utils';

import useSlice from '~/utils/slice/hook';

const IS_REQUIRED = 'is required';

const formEnhancer = withFormik({
  validationSchema: Yup.object().shape({
    org: Yup
      .mixed()
      .nullable()
      .required(IS_REQUIRED),
    fromDivision: Yup
      .mixed()
      .nullable()
      .required(IS_REQUIRED),
    toDivision: Yup
      .mixed()
      .nullable()
      .notOneOf([Yup.ref('fromDivision')], 'cannot be the same as from division')
      .required(IS_REQUIRED),
  }),

  mapPropsToValues: props => ({
    org: props && props.org,
    fromDivision: null,
    toDivision: null,
    startDate: null,
    endDate: null,
  }),

  handleSubmit: (values, { setErrors, setSubmitting }) => {
    const p = {
      orgUid: values.org.id,
      divisionUid: values.fromDivision.id,
      toDivisionUid: values.toDivision.id,
      startDate: values.startDate,
      endDate: values.endDate,
    };

    axiosAdmin
      .post('/associate-division', p)
      .then(() => toastSuccess())
      .then(() => window.location.reload())
      .catch(e => setErrors({ server: e.errors }))
      .then(() => setSubmitting(false));
  },

  displayName: 'AssociateDivisionsForm',
  validateOnChange: false,
  validateOnBlur: false,
});

const AssociateDivisions = ({
  values,
  setFieldValue,
  handleSubmit,
  isSubmitting,
}) => {
  const { fetch } = useSlice();
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [availableDivisions, setAvailableDivisions] = useState([]);

  const [existingAssociateDivisions, setExistingAssociateDivisions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch('organization')
      .then((r) => {
        setIsLoading(false);
        setAvailableOrganizations(r.data.data);
      });
  }, []);

  const refreshExistingAssociateDivisions = () =>
    axiosAdmin
      .get(`/associate-division?org-uid=${values.org.id}`)
      .then(data => data.data.data)
      .then(setExistingAssociateDivisions);

  useEffect(() => {
    setFieldValue('fromDivision', null);
    setFieldValue('toDivision', null);
    setFieldValue('startDate', null);
    setFieldValue('endDate', null);

    if (values.org) {
      refreshExistingAssociateDivisions();

      fetch('division', {
        'org-uid': values.org.id,
      })
        .then(r => r.data.data)
        .then(setAvailableDivisions);
    }
  }, [values.org]);

  // hack to refresh the list of items after handleSubmit since handleSubmit is
  // in the withFormik above and therefore cannot call
  // refreshExistingAssociateDivisions
  const [hack, setHack] = useState(false);
  useEffect(() => {
    if (isSubmitting) {
      setHack(true);
    } else if (hack) {
      setHack(false);
      refreshExistingAssociateDivisions();
    }
  }, [isSubmitting]);

  const deleteItem = (item) => {
    axiosAdmin
      .delete(`/associate-division/${item.id}`)
      .then(() => refreshExistingAssociateDivisions());
  };

  const tableColumns = [{
    id: 'fromDivision',
    Header: 'From Division',
    accessor: item => availableDivisions.length > 0 && availableDivisions.find(d =>
      item.attributes.divisionUid === d.id).attributes.divisionName,
  }, {
    id: 'toDivision',
    Header: 'To Division',
    accessor: item => availableDivisions.length > 0 && availableDivisions.find(d =>
      item.attributes.toDivisionUid === d.id).attributes.divisionName,
  }, {
    id: 'startDate',
    Header: 'Start Date',
    accessor: 'attributes.startDate',
    filterable: false,
  }, {
    id: 'endDate',
    Header: 'End Date',
    accessor: 'attributes.endDate',
    filterable: false,
  }, {
    id: 'delete',
    Header: 'Delete',
    accessor: () => 'Click here to delete',
    filterable: false,
  }];

  return (
    <>
      <PageTitle>Associate Divisions</PageTitle>

      <Card>
        <form onSubmit={handleSubmit} noValidate>
          <CardBody>
            <Row>
              <Col>
                <Field
                  name="org"
                  label="Organization"
                  type="select"
                  options={availableOrganizations}
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.orgName}
                  loading={isLoading}
                />
              </Col>
            </Row>

            {values.org && (
              <>
                <Row>
                  <Col>
                    <Field
                      name="fromDivision"
                      type="select"
                      options={availableDivisions}
                      getOptionValue={d => d.id}
                      getOptionLabel={d => d.attributes.divisionName}
                      required
                    />
                  </Col>

                  <Col>
                    <Field
                      name="toDivision"
                      type="select"
                      options={availableDivisions}
                      getOptionValue={d => d.id}
                      getOptionLabel={d => d.attributes.divisionName}
                      required
                    />
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Field
                      name="startDate"
                      type="date"
                    />
                  </Col>

                  <Col>
                    <Field
                      name="endDate"
                      type="date"
                    />
                  </Col>
                </Row>
              </>
            )}
          </CardBody>

          <CardFooter className="d-flex">
            <div className="ml-auto">
              <Button type="submit" disabled={!values.org && isSubmitting}>
                {isSubmitting ? 'Working ...' : 'Save'}
              </Button>
            </div>
          </CardFooter>
        </form>
      </Card>

      {values.org && (
        <>
          <br />

          <Table
            data={existingAssociateDivisions}
            columns={tableColumns}
            defaultSorted={[{ id: 'fromDivision' }]}
            filterable
            defaultFilterMethod={(filter, row) =>
              row[filter.id].toLowerCase().includes(filter.value.toLowerCase())
            }
            getTdProps={(state, rowInfo, column) => ({
                onClick: () => {
                  if (column.id === 'delete') {
                    deleteItem(rowInfo.original);
                  }
                },
              })}
          />
        </>
      )}
    </>
  );
};

export default formEnhancer(AssociateDivisions);
